import { GridSortModel } from '@mui/x-data-grid';
import { debounce } from 'lodash';
import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface IQueryParams {
    page?: number;
    pageSize?: number;
    filter: Record<string, any>;
    sort: GridSortModel;
    search: string;
}

const defaultQueryParams = (): IQueryParams => ({
    page: 1,
    pageSize: 10,
    search: '',
    sort: [{ field: 'name', sort: 'asc' }],
    filter: {},
});

export interface QueryParamsContextReturnType {
    queryParamsByEntity: Record<string, IQueryParams>;
    debouncedQueryParamsByEntity: Record<string, IQueryParams>;
    setQueryParamsForEntity: (entity: string, queryParams: IQueryParams) => void;
    setFilterForEntity: (entity: string, filter?: IQueryParams['filter']) => void;
    setPageForEntity: (entity: string, page: number) => void;
    setPageSizeForEntity: (entity: string, pageSize: number) => void;
    setSortForEntity: (entity: string, sort: GridSortModel) => void;
    setSearchForEntity: (entity: string, search: string) => void;
}

export interface QueryParamsHookReturnType {
    queryParams: IQueryParams;
    debouncedQueryParams: IQueryParams;
    setFilter: (filter?: IQueryParams['filter']) => void;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    setSort: (sort: GridSortModel) => void;
    setSearch: (search: string) => void;
    setQueryParams: (queryParams: IQueryParams) => void;
}

export const QueryParamsContext = createContext({ queryParamsByEntity: {} } as QueryParamsContextReturnType);

const ensureEntity = (
    record: Record<string, IQueryParams>,
    entity: string,
    overrides?: Partial<IQueryParams>,
): Record<string, IQueryParams> => {
    if (record[entity]) return record;
    return { ...record, [entity]: { ...defaultQueryParams(), ...overrides } };
};

const mergeEntityIntoRecord = (
    record: Record<string, IQueryParams>,
    entity: string,
    params: Partial<IQueryParams>,
): Record<string, IQueryParams> => {
    const withEntity = ensureEntity(record, entity);
    return { ...withEntity, [entity]: { ...withEntity[entity], ...params } };
};

interface Props {
    children?: ReactNode;
}

export const QueryParamsContextProvider: FC<Props> = ({ children }) => {
    const [queryParamsByEntity, setQueryParamsByEntity] = useState<QueryParamsContextReturnType['queryParamsByEntity']>(
        {},
    );
    const [debouncedQueryParamsByEntity, setDebouncedQueryParamsByEntity] = useState<
        QueryParamsContextReturnType['debouncedQueryParamsByEntity']
    >({});

    const handleSetDebounced = useMemo(
        () =>
            debounce((newQueryParamsByEntity: Record<string, IQueryParams>) => {
                setDebouncedQueryParamsByEntity(newQueryParamsByEntity);
            }, 300),
        [],
    );

    useEffect(() => {
        handleSetDebounced(queryParamsByEntity);
    }, [queryParamsByEntity, handleSetDebounced]);

    const setQueryParamsForEntity: QueryParamsContextReturnType['setQueryParamsForEntity'] = useCallback(
        (entity, queryParams) => {
            setQueryParamsByEntity((prev) => ({ ...prev, [entity]: queryParams }));
        },
        [],
    );

    const setPageForEntity: QueryParamsContextReturnType['setPageForEntity'] = useCallback((entity, page) => {
        setQueryParamsByEntity((prev) => mergeEntityIntoRecord(prev, entity, { page }));
    }, []);

    const setPageSizeForEntity: QueryParamsContextReturnType['setPageSizeForEntity'] = useCallback(
        (entity, pageSize) => {
            setQueryParamsByEntity((prev) => mergeEntityIntoRecord(prev, entity, { pageSize }));
        },
        [],
    );

    const setSortForEntity: QueryParamsContextReturnType['setSortForEntity'] = useCallback((entity, sort) => {
        setQueryParamsByEntity((prev) => mergeEntityIntoRecord(prev, entity, { sort }));
    }, []);

    const setSearchForEntity: QueryParamsContextReturnType['setSearchForEntity'] = useCallback((entity, search) => {
        setQueryParamsByEntity((prev) => mergeEntityIntoRecord(prev, entity, { search }));
    }, []);

    const setFilterForEntity: QueryParamsContextReturnType['setFilterForEntity'] = useCallback((entity, filter) => {
        setQueryParamsByEntity((prev) => mergeEntityIntoRecord(prev, entity, { filter }));
    }, []);

    const value: QueryParamsContextReturnType = {
        queryParamsByEntity,
        debouncedQueryParamsByEntity,
        setQueryParamsForEntity,
        setPageForEntity,
        setPageSizeForEntity,
        setSortForEntity,
        setSearchForEntity,
        setFilterForEntity,
    };

    return <QueryParamsContext.Provider value={value}>{children}</QueryParamsContext.Provider>;
};

export const useQueryParamsContext = (entity: string, overrides?: Partial<IQueryParams>): QueryParamsHookReturnType => {
    const {
        setQueryParamsForEntity,
        queryParamsByEntity,
        debouncedQueryParamsByEntity,
        setPageForEntity,
        setPageSizeForEntity,
        setSortForEntity,
        setSearchForEntity,
        setFilterForEntity,
    } = useContext(QueryParamsContext);

    useEffect(() => {
        if (!queryParamsByEntity[entity]) setQueryParamsForEntity(entity, { ...defaultQueryParams(), ...overrides });
    }, [entity]);

    const queryParams = useMemo(
        () => queryParamsByEntity[entity] ?? defaultQueryParams(),
        [queryParamsByEntity, entity],
    );

    const debouncedQueryParams = useMemo(
        () => debouncedQueryParamsByEntity[entity] ?? queryParams,
        [debouncedQueryParamsByEntity, entity, queryParams],
    );

    const setQueryParams: QueryParamsHookReturnType['setQueryParams'] = useCallback(
        (newQueryParams) => {
            setQueryParamsForEntity(entity, newQueryParams);
        },
        [entity, setQueryParamsForEntity],
    );

    const setPage: QueryParamsHookReturnType['setPage'] = useCallback(
        (page) => {
            setPageForEntity(entity, page);
        },
        [entity, setPageForEntity],
    );

    const setPageSize: QueryParamsHookReturnType['setPageSize'] = useCallback(
        (pageSize) => {
            setPageSizeForEntity(entity, pageSize);
        },
        [entity, setPageSizeForEntity],
    );

    const setSort: QueryParamsHookReturnType['setSort'] = useCallback(
        (sort) => {
            setSortForEntity(entity, sort);
        },
        [entity, setSortForEntity],
    );

    const setSearch: QueryParamsHookReturnType['setSearch'] = useCallback(
        (search) => {
            setSearchForEntity(entity, search);
        },
        [entity, setSearchForEntity],
    );

    const setFilter: QueryParamsHookReturnType['setFilter'] = useCallback(
        (filter) => {
            setFilterForEntity(entity, filter);
        },
        [entity, setFilterForEntity],
    );

    return {
        queryParams,
        debouncedQueryParams,
        setFilter,
        setPage,
        setPageSize,
        setSort,
        setSearch,
        setQueryParams,
    };
};
